import React from 'react';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  standardColours,
  brandColours,
  fontWeights,
  fontSize,
  visuallyHidden,
} from '../styles';
import { isOnSale, formatPrice } from '../utils';

const StyledPrice = styled.p`
  line-height: 1.2;
`;

const StyledOldPrice = styled.s`
  display: block;
  color: ${standardColours.darkerGrey};
  font-weight: ${fontWeights.semibold};
  ${({ isVariantPrice, isSearchItem, isCartCrossSellProduct }) =>
    isVariantPrice
      ? fontSize(18)
      : isSearchItem || isCartCrossSellProduct
        ? fontSize(14)
        : fontSize(16)};

  ${minBreakpointQuery.small`
    ${({ isVariantPrice, isSearchItem, isCartCrossSellProduct }) =>
      isVariantPrice
        ? fontSize(19)
        : isSearchItem || isCartCrossSellProduct
          ? fontSize(15)
          : fontSize(17)};
  `}

  ${minBreakpointQuery.large`
    ${({ isVariantPrice, isSearchItem, isCartCrossSellProduct }) =>
      isVariantPrice
        ? fontSize(20)
        : isSearchItem || isCartCrossSellProduct
          ? fontSize(16)
          : fontSize(18)};
  `}
`;

const StyledLabel = styled.span`
  ${visuallyHidden()};
`;

const StyledIncExVatPrice = styled.span`
  ${({ isSearchItem, isCartItem, isCartCrossSellProduct }) => {
    if ((isSearchItem || isCartItem, isCartCrossSellProduct)) {
      return css`
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      `;
    } else {
      return css`
        display: block;
      `;
    }
  }}
`;

const StyledPrimaryPrice = styled.span`
  color: ${brandColours.tertiary};
  font-weight: ${fontWeights.bold};
  ${({ isVariantPrice, isSearchItem, isCartItem, isCartCrossSellProduct }) =>
    isVariantPrice
      ? fontSize(24)
      : isSearchItem || isCartItem || isCartCrossSellProduct
        ? fontSize(16)
        : fontSize(20)};

  ${minBreakpointQuery.small`
    ${({ isVariantPrice, isSearchItem, isCartItem, isCartCrossSellProduct }) =>
      isVariantPrice
        ? fontSize(26)
        : isSearchItem || isCartItem || isCartCrossSellProduct
          ? fontSize(17)
          : fontSize(22)};
  `}

  ${minBreakpointQuery.large`
    ${({ isVariantPrice, isSearchItem, isCartItem, isCartCrossSellProduct }) =>
      isVariantPrice
        ? fontSize(28)
        : isSearchItem || isCartItem || isCartCrossSellProduct
          ? fontSize(18)
          : fontSize(24)};
  `}
`;

const StyledSecondaryPrice = styled.span`
  display: block;
  margin-top: 4px;
  color: ${standardColours.black};

  ${({ isVariantPrice, isSearchItem, isCartItem, isCartCrossSellProduct }) => {
    if (isVariantPrice) {
      return css`
        color: ${standardColours.transparentBlack(0.25)};
        font-weight: ${fontWeights.bold};
        ${fontSize(20)};

        ${minBreakpointQuery.small`
          ${fontSize(22)};
        `}

        ${minBreakpointQuery.large`
          ${fontSize(24)};
        `}
      `;
    } else if (isSearchItem || isCartItem || isCartCrossSellProduct) {
      return css`
        ${fontSize(12)};
      `;
    }
  }}
`;

export const IncExVatPrice = ({
  price,
  isVariantPrice,
  isSearchItem,
  isCartItem,
  isCartCrossSellProduct,
  isRetailProduct,
  ...props
}) => (
  <StyledIncExVatPrice
    isSearchItem={isSearchItem}
    isCartItem={isCartItem}
    isCartCrossSellProduct={isCartCrossSellProduct}
    {...props}
  >
    <StyledPrimaryPrice
      isVariantPrice={isVariantPrice}
      isSearchItem={isSearchItem}
      isCartItem={isCartItem}
      isCartCrossSellProduct={isCartCrossSellProduct}
    >
      {formatPrice(price)}
      {!isRetailProduct && ` ex VAT`}
    </StyledPrimaryPrice>
    {!isRetailProduct && (
      <StyledSecondaryPrice
        isVariantPrice={isVariantPrice}
        isSearchItem={isSearchItem}
        isCartItem={isCartItem}
        isCartCrossSellProduct={isCartCrossSellProduct}
      >
        {formatPrice(price * 1.2)} inc VAT
      </StyledSecondaryPrice>
    )}
  </StyledIncExVatPrice>
);

export const ProductPrice = ({
  priceRangeV2: { minVariantPrice, maxVariantPrice },
  variants,
  isSearchItem,
  isCartCrossSellProduct,
  isRetailProduct,
  ...props
}) => {
  const hasSalePrice = isOnSale(variants);

  let lowestCompareAtPrice;

  if (hasSalePrice) {
    const variantsOnSale = variants.filter(
      variant =>
        variant.compareAtPrice !== null &&
        parseFloat(variant.price) < parseFloat(variant.compareAtPrice),
    );

    if (variantsOnSale.length > 1) {
      variantsOnSale.sort(
        (a, b) => parseFloat(a.compareAtPrice) - parseFloat(b.compareAtPrice),
      );
    }

    lowestCompareAtPrice = variantsOnSale[0].compareAtPrice;
  }

  return (
    <StyledPrice {...props}>
      {hasSalePrice && (
        <StyledOldPrice
          isSearchItem={isSearchItem}
          isCartCrossSellProduct={isCartCrossSellProduct}
        >
          {formatPrice(lowestCompareAtPrice)}
        </StyledOldPrice>
      )}
      <StyledLabel>{hasSalePrice ? 'Sale price' : 'Regular price'}</StyledLabel>
      {minVariantPrice.amount !== maxVariantPrice.amount ? 'From ' : ''}
      <IncExVatPrice
        price={minVariantPrice.amount}
        isSearchItem={isSearchItem}
        isCartCrossSellProduct={isCartCrossSellProduct}
        isRetailProduct={isRetailProduct}
      />
    </StyledPrice>
  );
};

export const VariantPrice = ({ variant, isRetailProduct, ...props }) => {
  const hasSalePrice = isOnSale([variant]);
  const { compareAtPrice } = variant;

  return (
    <StyledPrice {...props}>
      {hasSalePrice && (
        <StyledOldPrice isVariantPrice={true}>
          {formatPrice(compareAtPrice)}
        </StyledOldPrice>
      )}
      <StyledLabel>{hasSalePrice ? 'Sale price' : 'Regular price'}</StyledLabel>
      <IncExVatPrice
        price={variant.price}
        isVariantPrice={true}
        isRetailProduct={isRetailProduct}
      />
    </StyledPrice>
  );
};
